import React, { useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import useDrawerTransition from "../../Hooks/useDrawerTransition";

const widthMap = {
  sm: "w-full sm:max-w-sm",
  md: "w-full sm:max-w-lg",
  lg: "w-full sm:w-[45vw] md:w-[50vw] lg:w-[55vw] sm:max-w-[900px]",
  xl: "w-full sm:w-[55vw] md:w-[60vw] lg:w-[65vw] sm:max-w-[1100px]",
};

function DrawerHeader({ children, className = "" }) {
  return <div className={`shrink-0 ${className}`}>{children}</div>;
}

function DrawerBody({ children, className = "" }) {
  return <div className={`flex-1 overflow-y-auto ${className}`}>{children}</div>;
}

function DrawerFooter({ children, className = "" }) {
  return <div className={`shrink-0 ${className}`}>{children}</div>;
}

function Drawer({
  open,
  onClose,
  title,
  subtitle,
  titleIcon,
  width = "md",
  position = "right",
  headerClassName = "",
  headerGradient = false,
  children,
  zIndex = 9999,
}) {
  const handleEsc = useCallback(
    (e) => {
      if (e.key === "Escape" && onClose) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleEsc);
    document.body.classList.add("overflow-hidden");
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.classList.remove("overflow-hidden");
    };
  }, [open, handleEsc]);

  // Kapalı pozisyonda mount → iki frame sonra açık pozisyon (giriş kayması);
  // kapanışta çıkış animasyonu bitene kadar DOM'da kalır.
  const { mounted, entered } = useDrawerTransition(open);

  // Kapanış boyunca içerik dondurulur: çağıran taraf onClose'da state'i
  // sıfırladığında (başlık, renk, seçili kayıt) drawer hâlâ ekranda kayarken
  // "varsayılana dönme" görüntüsü oluşuyordu.
  const frozenRef = useRef({ children, title, subtitle, titleIcon });
  useEffect(() => {
    if (open) frozenRef.current = { children, title, subtitle, titleIcon };
  });
  const view = open
    ? { children, title, subtitle, titleIcon }
    : frozenRef.current;

  if (!mounted) return null;

  const widthClass = widthMap[width] || widthMap.md;
  const positionBase = position === "left" ? "inset-y-0 left-0" : "inset-y-0 right-0";
  const translateOpen = "translate-x-0";
  const translateClosed = position === "left" ? "-translate-x-full" : "translate-x-full";

  const hasDefaultHeader = view.title || view.subtitle || view.titleIcon;
  const defaultHeaderBg = headerGradient
    ? "bg-gradient-to-r from-gray-800 to-slate-900 text-white"
    : "bg-slate-50/80 border-b border-slate-200";
  const defaultHeaderText = headerGradient ? "text-white" : "text-slate-800";
  const defaultSubtitleText = headerGradient ? "text-white/80" : "text-slate-500";
  const defaultCloseBtn = headerGradient
    ? "bg-white/10 hover:bg-white/20 text-white"
    : "text-slate-500 hover:text-slate-700 hover:bg-slate-200";

  return createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
        style={{ zIndex: zIndex - 1 }}
        onClick={onClose}
      />
      <div
        className={`fixed ${positionBase} ${widthClass} transform ${entered ? translateOpen : translateClosed} transition-transform duration-300 ease-in-out shadow-2xl flex flex-col bg-white`}
        style={{ zIndex }}
        role="dialog"
        aria-modal="true"
      >
        {hasDefaultHeader && (
          <div className={`shrink-0 px-5 py-4 ${defaultHeaderBg} ${headerClassName}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {view.titleIcon && (
                  <div className={`w-10 h-10 ${headerGradient ? "bg-white/20 backdrop-blur-sm" : "bg-slate-100"} rounded-xl flex items-center justify-center`}>
                    {view.titleIcon}
                  </div>
                )}
                <div>
                  {view.title && (
                    <h2 className={`text-base font-bold tracking-wide ${defaultHeaderText}`}>
                      {view.title}
                    </h2>
                  )}
                  {view.subtitle && (
                    <p className={`text-xs mt-0.5 ${defaultSubtitleText}`}>{view.subtitle}</p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${defaultCloseBtn}`}
                aria-label="Kapat"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {view.children}
      </div>
    </>,
    document.body
  );
}

Drawer.Header = DrawerHeader;
Drawer.Body = DrawerBody;
Drawer.Footer = DrawerFooter;

export default Drawer;
